/**
 * Registo central dos textos internos do painel (títulos e descrições das páginas).
 * Fonte de verdade dos valores padrão; overrides do Super Admin são guardados em
 * localStorage (ver `painel-texts-context`). Cada componente lê pelo `usePainelText(key)`.
 */

export type PainelTextValue = {
  title: string;
  description: string;
};

export type PainelTextEntry = PainelTextValue & {
  key: string;
  /** Id da secção no editor (agrupa as entradas). */
  sectionId: string;
  /** Rótulo curto mostrado no editor. */
  label: string;
};

export type PainelTextSection = {
  id: string;
  label: string;
};

export const PAINEL_TEXT_SECTIONS: PainelTextSection[] = [
  { id: 'content', label: 'Conteúdo' },
  { id: 'management', label: 'Gestão' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'pages', label: 'Editores de páginas' },
  { id: 'system', label: 'Sistema' },
];

export const PAINEL_TEXT_ENTRIES: PainelTextEntry[] = [
  {
    key: 'categorias',
    sectionId: 'content',
    label: 'Categorias',
    title: 'Categorias',
    description:
      'Mesmos cards da seção "Navegue por categoria" no site. Edite título, descrição, badge, cor e ícone — as alterações refletem na homepage.',
  },
  {
    key: 'guias',
    sectionId: 'content',
    label: 'Guias',
    title: 'Guias',
    description:
      'Artigos por categoria com blocos rich text. Nomes de powers no texto tornam-se links com preview no site público.',
  },
  {
    key: 'powers',
    sectionId: 'content',
    label: 'Powers',
    title: 'Powers',
    description:
      'Gerencie o catálogo da Powerpédia — textos, smilies, preços, tutorial e páginas Everypower / AllPowers. Use [[nome-do-power]] nos textos para menções com preview no site.',
  },
  {
    key: 'aces',
    sectionId: 'content',
    label: 'Aces',
    title: 'Aces',
    description: 'Gerencie coleções de aces, níveis, molduras e páginas públicas em /aces.',
  },
  {
    key: 'paginas',
    sectionId: 'content',
    label: 'Páginas (hub)',
    title: 'Páginas públicas',
    description:
      'Mapa do conteúdo editável do Help Center. Páginas institucionais guardam-se neste navegador (localStorage). Powers, guias e changelogs têm editores próprios no menu lateral — os cartões abaixo indicam o que cada secção controla no site público.',
  },
  {
    key: 'equipe',
    sectionId: 'management',
    label: 'Equipe',
    title: 'Equipe',
    description:
      'Aqui você monta a página da equipe: edite o cabeçalho, adicione membros e arraste cada card entre os cargos para reorganizar quem faz parte de cada grupo.',
  },
  {
    key: 'usuarios',
    sectionId: 'management',
    label: 'Usuários',
    title: 'Usuários',
    description: 'Equipe com acesso ao painel — dados simulados para desenvolvimento.',
  },
  {
    key: 'permissoes',
    sectionId: 'management',
    label: 'Permissões',
    title: 'Permissões',
    description:
      'Cargos e permissões do painel. Cargos customizados são salvos localmente neste navegador.',
  },
  {
    key: 'analytics_visao',
    sectionId: 'analytics',
    label: 'Analytics — Visão geral',
    title: 'Visão geral',
    description:
      'Indicadores agregados do Help Center — tráfego, buscas e conteúdo (MySQL + GA4 quando configurado).',
  },
  {
    key: 'analytics_trafego',
    sectionId: 'analytics',
    label: 'Analytics — Tráfego',
    title: 'Tráfego',
    description: 'Origem das visitas, dispositivos e volume por dia — ideal para afinar SEO e UX.',
  },
  {
    key: 'analytics_pesquisas',
    sectionId: 'analytics',
    label: 'Analytics — Pesquisas',
    title: 'Pesquisas',
    description:
      'Termos buscados, conversão nos guias e oportunidades de conteúdo (busca sem resultado).',
  },
  {
    key: 'analytics_guias',
    sectionId: 'analytics',
    label: 'Analytics — Guias',
    title: 'Guias',
    description:
      'Performance dos artigos do Help Center: visualizações, envolvimento e tempo de leitura.',
  },
  {
    key: 'analytics_powers',
    sectionId: 'analytics',
    label: 'Analytics — Powers',
    title: 'Powers',
    description:
      'Powers mais visitados, cliques em Abrir Power e Ver Smilies, categorias em destaque.',
  },
  {
    key: 'analytics_cms',
    sectionId: 'analytics',
    label: 'Analytics — CMS',
    title: 'CMS',
    description: 'Publicações, rascunhos, arquivados e atividade por editor no painel.',
  },
  {
    key: 'analytics_usuarios',
    sectionId: 'analytics',
    label: 'Analytics — Usuários',
    title: 'Usuários',
    description: 'Administradores, editores ativos, logins e publicações por membro da equipe.',
  },
  {
    key: 'pagina_homepage',
    sectionId: 'pages',
    label: 'Editor: Homepage — Hero',
    title: 'Homepage — Hero',
    description:
      'Título, descrição e botões do hero principal. As sugestões de pesquisa abrem resultados na homepage com a query configurada.',
  },
  {
    key: 'pagina_regras',
    sectionId: 'pages',
    label: 'Editor: Regras',
    title: 'Regras do xat',
    description:
      'Cabeçalho e seções públicas de /regras (HTML rico, categoria e flag de atualizado).',
  },
  {
    key: 'pagina_conduta',
    sectionId: 'pages',
    label: 'Editor: Código de conduta',
    title: 'Código de conduta',
    description:
      'Badge, título, subtítulo e itens do código de conduta em /conduta.',
  },
  {
    key: 'pagina_equipe_textos',
    sectionId: 'pages',
    label: 'Editor: Página da equipe — Textos',
    title: 'Página da equipe — Textos',
    description:
      'Hero, descrições por cargo, secção «Como se tornar membro» e CTA «Quer saber mais?» em /equipe. Membros são geridos em Equipe.',
  },
  {
    key: 'identidade',
    sectionId: 'system',
    label: 'Identidade do site',
    title: 'Identidade do site',
    description:
      'Título e subtítulo do site, favicon, e conteúdos do rodapé público (links, copyright, lojas e changelog).',
  },
  {
    key: 'coming_soon',
    sectionId: 'system',
    label: 'Coming Soon',
    title: 'Coming Soon',
    description:
      'Active o modo de manutenção pública, edite textos da página /coming-soon e pré-visualize antes de publicar no repositório.',
  },
];

export const PAINEL_TEXT_DEFAULTS: Record<string, PainelTextValue> = Object.fromEntries(
  PAINEL_TEXT_ENTRIES.map((e) => [e.key, { title: e.title, description: e.description }])
);

export function getPainelTextDefault(key: string): PainelTextValue {
  return PAINEL_TEXT_DEFAULTS[key] ?? { title: '', description: '' };
}
